import React from "react";
import styled from "styled-components";
import Page from "../../components/Page";
import Async from "../../components/Async";
import api from "../../services/api";
import { Button } from "../../components/buttons";
import { borderColor, spacing } from "../../components/layout";

const AUTO_REFRESH_MS = 120000;
const RECENT_LIMIT = 40;

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${spacing};
  margin-bottom: 20px;
`;

const ToolbarText = styled.div`
  max-width: 700px;
  line-height: 1.45;
`;

const Muted = styled.div`
  color: var(--muted-text);
  font-size: 13px;
  margin-top: 4px;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: ${spacing};
  margin-bottom: 24px;
`;

const SummaryCard = styled.div`
  border: 1px solid ${borderColor};
  border-radius: 8px;
  padding: 16px;
  background: var(--surface-bg);
`;

const SummaryValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  margin-top: 6px;
`;

const SummaryLabel = styled.div`
  color: var(--muted-text);
  font-size: 13px;
`;

const SectionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: ${spacing};
  margin-bottom: 26px;
`;

const SectionCard = styled.div`
  border: 1px solid ${borderColor};
  border-radius: 8px;
  padding: 16px;
  background: var(--surface-bg);
`;

const SectionTitle = styled.div`
  font-weight: 700;
  margin-bottom: 14px;
`;

const BreakdownRow = styled.div`
  display: grid;
  grid-template-columns: minmax(90px, 1fr) 70px;
  gap: 10px;
  align-items: center;
  margin-bottom: 10px;
`;

const BreakdownLabel = styled.div`
  min-width: 0;
`;

const BreakdownLabelText = styled.div`
  font-size: 13px;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BreakdownBar = styled.div`
  width: 100%;
  height: 8px;
  background: rgba(0, 0, 0, 0.06);
  border-radius: 999px;
  overflow: hidden;
`;

const BreakdownFill = styled.div`
  height: 100%;
  width: ${props => props.width}%;
  background: #54d2b1;
  border-radius: 999px;
`;

const BreakdownCount = styled.div`
  text-align: right;
  font-weight: 600;
`;

const EmptyState = styled.div`
  color: var(--muted-text);
  font-size: 13px;
`;

const MeetupList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing};
`;

const MeetupCard = styled.details`
  border: 1px solid ${borderColor};
  border-radius: 10px;
  background: var(--surface-bg);
  overflow: hidden;
`;

const MeetupSummary = styled.summary`
  list-style: none;
  cursor: pointer;
  padding: 16px;
  display: flex;
  gap: 14px;
  align-items: flex-start;

  &::-webkit-details-marker {
    display: none;
  }
`;

const LocationThumb = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 8px;
  background: #f3f3f3;
  overflow: hidden;
  flex: 0 0 52px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const MeetupSummaryBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const MeetupTitleRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-bottom: 6px;
`;

const MeetupTitle = styled.div`
  font-weight: 700;
  font-size: 15px;
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 3px 9px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: ${props => props.background || "rgba(0,0,0,0.07)"};
  color: ${props => props.color || "#1d1d1d"};
`;

const SummaryMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  color: var(--muted-text);
  font-size: 13px;
`;

const MeetupBody = styled.div`
  border-top: 1px solid ${borderColor};
  padding: 16px;
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
`;

const MetaCard = styled.div`
  border: 1px solid ${borderColor};
  border-radius: 8px;
  padding: 12px;
  background: var(--surface-bg);
`;

const MetaLabel = styled.div`
  font-size: 12px;
  color: var(--muted-text);
  margin-bottom: 6px;
`;

const MetaValue = styled.div`
  font-weight: 600;
  line-height: 1.4;
  word-break: break-word;
`;

const Caption = styled.div`
  border-left: 3px solid #54d2b1;
  background: rgba(84, 210, 177, 0.08);
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 16px;
  line-height: 1.5;
`;

const ParticipantList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
`;

const ParticipantCard = styled.div`
  border: 1px solid ${borderColor};
  border-radius: 8px;
  padding: 12px;
  background: var(--surface-bg);
`;

const ParticipantHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
`;

const ParticipantThumb = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 999px;
  overflow: hidden;
  background: #f3f3f3;
  flex: 0 0 36px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const ParticipantName = styled.div`
  font-weight: 600;
`;

const ParticipantMeta = styled.div`
  color: var(--muted-text);
  font-size: 12px;
  margin-top: 2px;
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.06);
  font-size: 12px;
`;

const SmallBlock = styled.div`
  margin-top: 10px;
  color: var(--muted-text);
  font-size: 12px;
  line-height: 1.45;
`;

const DetailFooter = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 12px;
`;

const FooterPanel = styled.div`
  border: 1px solid ${borderColor};
  border-radius: 8px;
  padding: 12px;
  background: var(--surface-bg);
`;

const FooterTitle = styled.div`
  font-weight: 700;
  margin-bottom: 8px;
`;

const FooterText = styled.div`
  font-size: 13px;
  line-height: 1.5;
  color: var(--muted-text);
  word-break: break-word;
`;

const prettyLabel = value => {
  if (!value) {
    return "Unknown";
  }
  return String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, char => char.toUpperCase());
};

const formatDateTime = value => {
  if (!value) {
    return " - ";
  }
  return new Date(value).toLocaleString();
};

const formatDistance = distanceMeters => {
  if (!distanceMeters && distanceMeters !== 0) {
    return null;
  }
  if (distanceMeters >= 1000) {
    return `${(distanceMeters / 1000).toFixed(1)} km`;
  }
  return `${distanceMeters} m`;
};

const statusColors = {
  now: { background: "rgba(84, 210, 177, 0.16)", color: "#0a6b53" },
  started: { background: "rgba(59, 130, 246, 0.16)", color: "#1d4ed8" },
  confirmed: { background: "rgba(52, 211, 153, 0.16)", color: "#047857" },
  partial: { background: "rgba(245, 158, 11, 0.18)", color: "#92400e" },
  pending: { background: "rgba(107, 114, 128, 0.16)", color: "#374151" },
  cancelled: { background: "rgba(239, 68, 68, 0.15)", color: "#b91c1c" },
  expired: { background: "rgba(0, 0, 0, 0.08)", color: "#4b5563" },
  empty: { background: "rgba(0, 0, 0, 0.08)", color: "#4b5563" }
};

const BreakdownList = ({ title, items }) => {
  const maxCount = items.reduce((max, item) => Math.max(max, item.count), 0);

  return (
    <SectionCard>
      <SectionTitle>{title}</SectionTitle>
      {!items.length ? (
        <EmptyState>No data yet.</EmptyState>
      ) : (
        items.map(item => (
          <BreakdownRow key={`${title}-${item.label}`}>
            <BreakdownLabel>
              <BreakdownLabelText>{prettyLabel(item.label)}</BreakdownLabelText>
              <BreakdownBar>
                <BreakdownFill
                  width={maxCount ? Math.max((item.count / maxCount) * 100, 6) : 0}
                />
              </BreakdownBar>
            </BreakdownLabel>
            <BreakdownCount>{item.count}</BreakdownCount>
          </BreakdownRow>
        ))
      )}
    </SectionCard>
  );
};

const RecentMeetupItem = ({ meetup, defaultOpen }) => {
  const location = meetup.location || {};
  const participantSummary = meetup.participant_summary || {};
  const messageSummary = meetup.message_summary || {};
  const liveLocationShares = meetup.live_location_shares || [];
  const routeDistance = formatDistance(meetup.route_distance_meters);
  const statusStyle = statusColors[meetup.status] || statusColors.pending;

  return (
    <MeetupCard open={defaultOpen}>
      <MeetupSummary>
        <LocationThumb>
          {location.main_photo ? <img src={location.main_photo} alt={location.name || "Meetup"} /> : null}
        </LocationThumb>
        <MeetupSummaryBody>
          <MeetupTitleRow>
            <MeetupTitle>
              #{meetup.id} {prettyLabel(meetup.activity)}
            </MeetupTitle>
            <Pill background={statusStyle.background} color={statusStyle.color}>
              {prettyLabel(meetup.status)}
            </Pill>
            {meetup.intent ? <Pill>{prettyLabel(meetup.intent)}</Pill> : null}
            {meetup.location_type ? <Pill>{prettyLabel(meetup.location_type)}</Pill> : null}
          </MeetupTitleRow>
          <SummaryMeta>
            <div>Created {formatDateTime(meetup.created_at)}</div>
            <div>Meetup {formatDateTime(meetup.meetup_time)}</div>
            <div>{location.name || "No location label"}</div>
            <div>{participantSummary.active_count || 0} active participants</div>
            <div>{messageSummary.count || 0} messages</div>
            {routeDistance ? <div>{routeDistance}</div> : null}
          </SummaryMeta>
        </MeetupSummaryBody>
      </MeetupSummary>

      <MeetupBody>
        {meetup.ai_caption || meetup.teaser ? (
          <Caption>{meetup.ai_caption || meetup.teaser}</Caption>
        ) : null}

        <MetaGrid>
          <MetaCard>
            <MetaLabel>Location</MetaLabel>
            <MetaValue>
              {location.name || "Unknown"}
              {routeDistance ? ` | ${routeDistance}` : ""}
              {meetup.route_is_loop ? " | Loop" : ""}
            </MetaValue>
          </MetaCard>
          <MetaCard>
            <MetaLabel>Participants</MetaLabel>
            <MetaValue>
              {participantSummary.approved_active_count || 0}/
              {participantSummary.active_count || 0} approved active
            </MetaValue>
          </MetaCard>
          <MetaCard>
            <MetaLabel>Surface / Credit</MetaLabel>
            <MetaValue>
              {participantSummary.active_surface_count || 0} surfaced |{" "}
              {participantSummary.held_credit_count || 0} held credits
            </MetaValue>
          </MetaCard>
          <MetaCard>
            <MetaLabel>Messages / Live Shares</MetaLabel>
            <MetaValue>
              {messageSummary.count || 0} messages |{" "}
              {participantSummary.live_location_live_count || 0} live shares
            </MetaValue>
          </MetaCard>
          <MetaCard>
            <MetaLabel>Plan</MetaLabel>
            <MetaValue>
              {prettyLabel(meetup.plan_type)} {meetup.is_now ? " | Now" : ""}
            </MetaValue>
          </MetaCard>
          <MetaCard>
            <MetaLabel>Compatibility</MetaLabel>
            <MetaValue>
              {meetup.compatibility_score || meetup.compatibility_score === 0
                ? meetup.compatibility_score
                : " - "}
            </MetaValue>
          </MetaCard>
        </MetaGrid>

        <SectionTitle>Participants</SectionTitle>
        {!meetup.participants.length ? (
          <EmptyState>No participants found.</EmptyState>
        ) : (
          <ParticipantList>
            {meetup.participants.map(participant => (
              <ParticipantCard key={`${meetup.id}-${participant.user_id}`}>
                <ParticipantHeader>
                  <ParticipantThumb>
                    {participant.profile_thumbnail_url ? (
                      <img
                        src={participant.profile_thumbnail_url}
                        alt={participant.name || `User ${participant.user_id}`}
                      />
                    ) : null}
                  </ParticipantThumb>
                  <div>
                    <ParticipantName>{participant.name || `User ${participant.user_id}`}</ParticipantName>
                    <ParticipantMeta>
                      {participant.age ? `${participant.age} years old` : "Age unknown"}
                    </ParticipantMeta>
                  </div>
                </ParticipantHeader>

                <ChipRow>
                  <Chip>{prettyLabel(participant.response_state)}</Chip>
                  {participant.surface_is_active ? <Chip>Surface active</Chip> : null}
                  {participant.meetup_credit_hold_status ? (
                    <Chip>{prettyLabel(participant.meetup_credit_hold_status)}</Chip>
                  ) : null}
                </ChipRow>

                <SmallBlock>
                  Joined {formatDateTime(participant.joined_at)}
                  {participant.selected_place_name
                    ? ` | Selected ${participant.selected_place_name}`
                    : ""}
                </SmallBlock>
                <SmallBlock>
                  {participant.surface_expires_at
                    ? `Surface expires ${formatDateTime(participant.surface_expires_at)}`
                    : "No active surface expiry"}
                </SmallBlock>
              </ParticipantCard>
            ))}
          </ParticipantList>
        )}

        <DetailFooter>
          <FooterPanel>
            <FooterTitle>Message State</FooterTitle>
            <FooterText>
              {messageSummary.last_message_text
                ? `"${messageSummary.last_message_text}"`
                : "No messages yet."}
            </FooterText>
            <Muted>
              {messageSummary.last_message_at
                ? `Last message ${formatDateTime(messageSummary.last_message_at)}`
                : "No last message timestamp"}
              {messageSummary.last_message_user_name
                ? ` by ${messageSummary.last_message_user_name}`
                : ""}
            </Muted>
          </FooterPanel>

          <FooterPanel>
            <FooterTitle>Live Location</FooterTitle>
            {liveLocationShares.length ? (
              <FooterText>
                {liveLocationShares
                  .map(share =>
                    `${share.name || `User ${share.user_id}`} (${share.is_live ? "live" : "stale"})`
                  )
                  .join(", ")}
              </FooterText>
            ) : (
              <FooterText>No live location shares for this meetup.</FooterText>
            )}
          </FooterPanel>
        </DetailFooter>
      </MeetupBody>
    </MeetupCard>
  );
};

class AIMeetups extends React.Component {
  state = {
    reload: false,
    lastRefreshedAt: null
  };

  componentDidMount() {
    this.interval = setInterval(this.refresh, AUTO_REFRESH_MS);
  }

  componentWillUnmount() {
    clearInterval(this.interval);
  }

  loadDashboard = async () => {
    const dashboard = await api.ai_meetups.dashboard({ recent_limit: RECENT_LIMIT });
    this.setState({ lastRefreshedAt: new Date() });
    return dashboard;
  };

  refresh = () => {
    this.setState(state => ({ reload: !state.reload }));
  };

  renderOverview = overview => {
    const cards = [
      { label: "Current scope meetups", value: overview.current_scope_count },
      { label: "Created last 24h", value: overview.created_last_24h },
      { label: "Created last 7d", value: overview.created_last_7d },
      { label: "Messages last 24h", value: overview.messages_last_24h },
      { label: "Live location shares", value: overview.live_location_users },
      { label: "Active surfaces", value: overview.active_surface_count },
      { label: "Held credits", value: overview.held_credit_count },
      { label: "Total AI meetups", value: overview.total_meetups }
    ];

    return (
      <SummaryGrid>
        {cards.map(card => (
          <SummaryCard key={card.label}>
            <SummaryLabel>{card.label}</SummaryLabel>
            <SummaryValue>{card.value || 0}</SummaryValue>
          </SummaryCard>
        ))}
      </SummaryGrid>
    );
  };

  renderDashboard = dashboard => {
    const breakdowns = dashboard.breakdowns || {};
    const recentMeetups = dashboard.recent_meetups || [];

    return (
      <React.Fragment>
        {this.renderOverview(dashboard.overview || {})}

        <SectionGrid>
          <BreakdownList title="Current Status" items={breakdowns.by_status || []} />
          <BreakdownList title="Activities (7d)" items={breakdowns.by_activity || []} />
          <BreakdownList title="Intents (7d)" items={breakdowns.by_intent || []} />
          <BreakdownList title="Location Types (7d)" items={breakdowns.by_location_type || []} />
          <BreakdownList title="Plan Types (7d)" items={breakdowns.by_plan_type || []} />
          <BreakdownList title="Creation Trend (7d)" items={breakdowns.created_last_7d || []} />
        </SectionGrid>

        <SectionCard>
          <SectionTitle>Recent AI Meetups</SectionTitle>
          {!recentMeetups.length ? (
            <EmptyState>No AI meetups found yet.</EmptyState>
          ) : (
            <MeetupList>
              {recentMeetups.map((meetup, index) => (
                <RecentMeetupItem
                  key={meetup.id}
                  meetup={meetup}
                  defaultOpen={index < 3 || meetup.status === "now"}
                />
              ))}
            </MeetupList>
          )}
        </SectionCard>
      </React.Fragment>
    );
  };

  render() {
    const { reload, lastRefreshedAt } = this.state;

    return (
      <Page title="AI Meetups">
        <Toolbar>
          <ToolbarText>
            Real-time-ish snapshot of the AI meetup flow. This refreshes automatically every 2 minutes
            so you can see what is being created, whether users have approved, and whether chat or live
            location has started.
            <Muted>
              {lastRefreshedAt
                ? `Last refreshed ${lastRefreshedAt.toLocaleString()}`
                : "Waiting for first refresh"}
            </Muted>
          </ToolbarText>
          <Button onClick={this.refresh}>Refresh now</Button>
        </Toolbar>

        <Async fetchProps={this.loadDashboard} reload={reload}>
          {this.renderDashboard}
        </Async>
      </Page>
    );
  }
}

export default AIMeetups;
